import { Dialog, DialogTitle, DialogContent, DialogActions, FormGroup } from "@mui/material";
import { useCallback, useEffect, useMemo } from "react";
import { UseFormReturn } from "react-hook-form";
import { FormDatePickerInput } from "../../../../../../common/components/masked_inputs/react_forms/form_date_picker_input";
import { DefaultFormMultiAutocomplete } from "../../../../../../common/components/default_form_multi_autocomplete";
import { SubcontaDropdown } from "../../../../domain/models/subconta_dropdown";
import { CentroCustoDropDown } from "../../../../../../common/domain/models/centro_custo_dropdown";
import { FuncionarioDropDown } from "../../../../../../common/domain/models/funcionario_dropdown";
import { FolhaPagamentoDropDown } from "../../../../domain/models/folha_pagamento_dropdown";
import { BancoPagamentoDropDown } from "../../../../domain/models/banco_pagamento_dropdown";
import { FiltroFolhaPagamento } from "../../../../domain/models/filtro_folha_pagamento";
import { ReportFolhaPagamentoForm, ReportFolhaPagamentoColunas } from "../../../schemas/FolhaPagamento_lista/report_folha_pagamento";
import { ModalLoading } from "../../../../../../common/components/modal_loading";
import { TableOptions } from "../../../../../../common/components/table_options";

import FormContainer from "../../../../../../common/components/form_container";
import PrimaryButton from "../../../../../../common/components/primary_button";
import DefaultFormInput from "../../../../../../common/components/default_form_input";

/**
 * ReportFolhaPagamentoModal
 */

type Props = {
  open: boolean,
  loading: boolean,
  savedColunas: boolean,
  funcionarios: FuncionarioDropDown[],
  subcontas: SubcontaDropdown[],
  centroCustos: CentroCustoDropDown[],
  folhaPagamentos: FolhaPagamentoDropDown[],
  bancoPagamentos: BancoPagamentoDropDown[],
  filtroFolhaPagamento: FiltroFolhaPagamento | null,
  hookForm: UseFormReturn<ReportFolhaPagamentoForm>,
  onClose: () => void,
  onSubmit: (form: ReportFolhaPagamentoForm) => void,
  onSaveColunas: () => void,
};

export const ReportFolhaPagamentoModal = ({
  open,
  loading,
  savedColunas,
  funcionarios,
  subcontas,
  centroCustos,
  folhaPagamentos,
  bancoPagamentos,
  filtroFolhaPagamento,
  hookForm,
  onClose,
  onSubmit,
  onSaveColunas,
}: Props) => {
  const [colunas] = hookForm.watch(['colunas'])

  const allSelected = useMemo(() => {
    return (
      colunas.nro &&
      colunas.centroCusto &&
      colunas.folhaPagamento &&
      colunas.funcionarioFolha &&
      colunas.bancoPagamento &&
      colunas.notaFiscal &&
      colunas.nroDoc &&
      colunas.responsavel &&
      colunas.status &&
      colunas.titulo &&
      colunas.subConta &&
      colunas.dataPagar &&
      colunas.dataPago &&
      colunas.valorPagar &&
      colunas.valorPago &&
      colunas.observacoes
    );
  }, [
    colunas.nro,
    colunas.centroCusto,
    colunas.folhaPagamento,
    colunas.funcionarioFolha,
    colunas.bancoPagamento,
    colunas.notaFiscal,
    colunas.nroDoc,
    colunas.responsavel,
    colunas.status,
    colunas.titulo,
    colunas.subConta,
    colunas.dataPagar,
    colunas.dataPago,
    colunas.valorPagar,
    colunas.valorPago,
    colunas.observacoes,
  ]);

  useEffect(() => {
    if (open) {
      if (filtroFolhaPagamento) {
        if (filtroFolhaPagamento.colunas && filtroFolhaPagamento.colunas.length > 0) {
          hookForm.setValue('colunas', {
            nro: filtroFolhaPagamento.colunas.includes('NR_CONTAS_PAGAR'),
            centroCusto: filtroFolhaPagamento.colunas.includes('CENTRO_CUSTO'),
            folhaPagamento: filtroFolhaPagamento.colunas.includes('FOLHA'),
            funcionarioFolha: filtroFolhaPagamento.colunas.includes('FUNCIONARIO_FOLHA'),
            bancoPagamento: filtroFolhaPagamento.colunas.includes('BANCO_PAGAMENTO'),
            notaFiscal: filtroFolhaPagamento.colunas.includes('NOTA_FISCAL'),
            nroDoc: filtroFolhaPagamento.colunas.includes('NRO_DOCUMENTO'),
            responsavel: filtroFolhaPagamento.colunas.includes('RESPONSAVEL'),
            status: filtroFolhaPagamento.colunas.includes('STATUS'),
            titulo: filtroFolhaPagamento.colunas.includes('TITULO'),
            subConta: filtroFolhaPagamento.colunas.includes('SUBCONTA'),
            dataPagar: filtroFolhaPagamento.colunas.includes('DATA_A_PAGAR'),
            dataPago: filtroFolhaPagamento.colunas.includes('DATA_PAGO'),
            valorPagar: filtroFolhaPagamento.colunas.includes('VALOR_A_PAGAR'),
            valorPago: filtroFolhaPagamento.colunas.includes('VALOR_PAGO'),
            observacoes: filtroFolhaPagamento.colunas.includes('OBSERVACOES'),
          });
        }
      } else {
        hookForm.reset();
      }
    }
  }, [
    hookForm,
    filtroFolhaPagamento,
    open,
  ]);

  const handleClose = useCallback(() => {
    onClose();
  }, [onClose]);

  const handleToggleSelectAll = useCallback((checked: boolean) => {
    if (checked) {
      hookForm.setValue('colunas', {
        nro: true,
        centroCusto: true,
        folhaPagamento: true,
        funcionarioFolha: true,
        bancoPagamento: true,
        notaFiscal: true,
        nroDoc: true,
        responsavel: true,
        status: true,
        titulo: true,
        subConta: true,
        dataPagar: true,
        dataPago: true,
        valorPagar: true,
        valorPago: true,
        observacoes: true,
      });
    } else {
      hookForm.setValue('colunas', {
        nro: false,
        centroCusto: false,
        folhaPagamento: false,
        funcionarioFolha: false,
        bancoPagamento: false,
        notaFiscal: false,
        nroDoc: false,
        responsavel: false,
        status: false,
        titulo: false,
        subConta: false,
        dataPagar: false,
        dataPago: false,
        valorPagar: false,
        valorPago: false,
        observacoes: false,
      });
    }
  }, [
    hookForm,
  ]);

  const handleToggleOptionChange = useCallback((field: keyof ReportFolhaPagamentoColunas, checked: boolean) => {
    hookForm.setValue(`colunas.${field}`, checked);
  }, [
    hookForm,
  ]);

  return (
    <Dialog
      open={open}
      slotProps={{
        paper: {
          component: 'form',
          onSubmit: hookForm.handleSubmit(onSubmit),
        }
      }}
      sx={{
        "& .MuiDialog-container": {
          "& .MuiPaper-root": {
            width: "100%",
            maxWidth: "65rem",
          },
          "& .MuiFormControlLabel-label": {
            fontStyle: 'normal',
            fontWeight: 400,
            fontSize: '14px',
          },
          "& .MuiCheckbox-root": {
            padding: '.25rem',
          }
        },
      }}>
      <DialogTitle>
        Relatorio Folha de Pagamento
      </DialogTitle>

      <DialogContent>
        <ModalLoading loading={loading} message="Processando">
          <div className="w-full grid grid-cols-6 grid-rows-1 gap-2 p-4">
            <div className="col-span-4">
              <FormContainer title={"Informações"} arrangement="fullRow" border={false}>
                <>
                  <DefaultFormInput
                    name="titulo"
                    label="Título"
                    reactHookForm={hookForm} />

                  <DefaultFormMultiAutocomplete
                    name='subcontas'
                    label='Subconta'
                    options={subcontas}
                    getOptionKey={(x) => x.id}
                    getOptionLabel={(x) => x.title}
                    reactHookForm={hookForm} />

                  <DefaultFormMultiAutocomplete
                    name='folhaPagamentos'
                    label='Folha de Pagamento'
                    options={folhaPagamentos}
                    getOptionKey={(x) => x.idFolhaPagamento}
                    getOptionLabel={(x) => x.descricao}
                    reactHookForm={hookForm} />

                  <DefaultFormMultiAutocomplete
                    name='centroCustos'
                    label='Centro de Custo'
                    options={centroCustos}
                    getOptionKey={(x) => x.id}
                    getOptionLabel={(x) => x.title}
                    reactHookForm={hookForm} />

                  <DefaultFormMultiAutocomplete
                    name='funcionarios'
                    label='Funcionário'
                    options={funcionarios}
                    getOptionKey={x => x.idFuncionario}
                    getOptionLabel={(x) => x.nome}
                    reactHookForm={hookForm} />

                  <DefaultFormMultiAutocomplete
                    name='bancoPagamentos'
                    label='Banco de Pagamento'
                    options={bancoPagamentos}
                    getOptionKey={(x) => x.idBancoPagamento}
                    getOptionLabel={(x) => x.descricao}
                    reactHookForm={hookForm} />

                  <DefaultFormMultiAutocomplete
                    name='status'
                    label='Status'
                    options={[
                      { id: 0, nome: 'Em Aberto' },
                      { id: 1, nome: 'Pagas' },
                      { id: 2, nome: 'Outros' },
                      { id: 3, nome: 'Atrasadas' }
                    ]}
                    getOptionKey={(x) => x.id}
                    getOptionLabel={(x) => x.nome}
                    reactHookForm={hookForm} />

                  <div className="w-full flex gap-1">
                    <FormDatePickerInput
                      name='dataPagarDe'
                      label='Data a Pagar De'
                      reactHookForm={hookForm} />

                    <FormDatePickerInput
                      name='dataPagarAte'
                      label='Data a Pagar Ate'
                      reactHookForm={hookForm} />

                  </div>

                  <div className="w-full flex gap-1">
                    <FormDatePickerInput
                      name='dataPagoDe'
                      label='Data Pago De'
                      reactHookForm={hookForm} />

                    <FormDatePickerInput
                      name='dataPagoAte'
                      label='Data Pago Ate'
                      reactHookForm={hookForm} />

                  </div>
                </>
              </FormContainer>
            </div>

            <div className="col-start-5 col-span-2 px-8 py-8">
              <FormGroup>
                <TableOptions label="EXIBIR TODAS AS COLUNAS" checked={allSelected} onChange={(c: boolean) => handleToggleSelectAll(c)} />

                <div className="flex flex-col pl-4">
                  <TableOptions checked={colunas.nro} label="Nº" onChange={(c: boolean) => handleToggleOptionChange('nro', c)} />
                  <TableOptions checked={colunas.centroCusto} label="CENTRO CUSTO" onChange={(c: boolean) => handleToggleOptionChange('centroCusto', c)} />
                  <TableOptions checked={colunas.folhaPagamento} label="FOLHA DE PAGAMENTO" onChange={(c: boolean) => handleToggleOptionChange('folhaPagamento', c)} />
                  <TableOptions checked={colunas.funcionarioFolha} label="FUNCIONÁRIO FOLHA" onChange={(c: boolean) => handleToggleOptionChange('funcionarioFolha', c)} />
                  <TableOptions checked={colunas.bancoPagamento} label="BANCO DE PAGAMENTO" onChange={(c: boolean) => handleToggleOptionChange('bancoPagamento', c)} />
                  <TableOptions checked={colunas.notaFiscal} label="NOTA FISCAL" onChange={(c: boolean) => handleToggleOptionChange('notaFiscal', c)} />
                  <TableOptions checked={colunas.nroDoc} label="Nº DOC" onChange={(c: boolean) => handleToggleOptionChange('nroDoc', c)} />
                  <TableOptions checked={colunas.responsavel} label="RESPONSÁVEL" onChange={(c: boolean) => handleToggleOptionChange('responsavel', c)} />
                  <TableOptions checked={colunas.status} label="STATUS" onChange={(c: boolean) => handleToggleOptionChange('status', c)} />
                  <TableOptions checked={colunas.titulo} label="TÍTULO" onChange={(c: boolean) => handleToggleOptionChange('titulo', c)} />
                  <TableOptions checked={colunas.subConta} label="SUBCONTA" onChange={(c: boolean) => handleToggleOptionChange('subConta', c)} />
                  <TableOptions checked={colunas.dataPagar} label="DATA A PAGAR" onChange={(c: boolean) => handleToggleOptionChange('dataPagar', c)} />
                  <TableOptions checked={colunas.dataPago} label="DATA PAGO" onChange={(c: boolean) => handleToggleOptionChange('dataPago', c)} />
                  <TableOptions checked={colunas.valorPagar} label="VALOR A PAGAR" onChange={(c: boolean) => handleToggleOptionChange('valorPagar', c)} />
                  <TableOptions checked={colunas.valorPago} label="VALOR PAGO" onChange={(c: boolean) => handleToggleOptionChange('valorPago', c)} />
                  <TableOptions checked={colunas.observacoes} label="OBSERVAÇÕES" onChange={(c: boolean) => handleToggleOptionChange('observacoes', c)} />
                </div>
              </FormGroup>
            </div>
          </div>
        </ModalLoading>
      </DialogContent>

      <DialogActions>
        <div className="w-full flex flex-col">
          <div className="w-full h-[1.25rem] flex justify-center items-center text-red-500 text-sm">
            {
              savedColunas &&
              "As informações das colunas selecionadas foram salvas com sucesso!"
            }
          </div>

          <div className="w-full flex-1 items-center justify-between flex p-4 gap-4">
            <div className="w-[8rem]">
              <PrimaryButton
                onClick={onSaveColunas}
                buttonColor="primary-green">
                Salvar Colunas
              </PrimaryButton>
            </div>

            <div className="flex flex-row gap-2">
              <div className="p-2 hover:bg-hover-light-gray cursor-pointer dark:hover:bg-border-dark-gray rounded-[4px] font-semibold">
                <h1
                  className="text-sm text-black dark:text-white"
                  onClick={handleClose}>
                  Cancelar
                </h1>
              </div>

              <div className="w-[120px]">
                {
                  loading ? (
                    <PrimaryButton
                      buttonColor="primary-green">
                      Gerando...
                    </PrimaryButton>
                  ) : (
                    <PrimaryButton
                      type="submit"
                      buttonColor="primary-green">
                      Gerar
                    </PrimaryButton>
                  )
                }
              </div>
            </div>
          </div>
        </div>
      </DialogActions>
    </Dialog>
  );
}